// Renders the current frame's image for one side, via the bounded, prefetching
// secpose image cache (authed fetch → blob object URL). See secposeImageCache.ts
// for why <img src> can't hit the worker directly and how memory is bounded.

import * as React from 'react';
import { loadSecPoseImage } from './secposeImageCache';
import { useStore } from './store';
import type { Side } from './store/predictions';

export function ImageCanvas({ side }: { side: Side }): React.ReactElement {
  const currentFrame = useStore((s) => s.currentFrame);
  const url = useStore((s) => s.imageUrls[side]?.[currentFrame]);
  const setSideImageSize = useStore((s) => s.setSideImageSize);

  const [src, setSrc] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
    if (!url) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    void loadSecPoseImage(url)
      .then((obj) => {
        if (!cancelled) setSrc(obj);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('secpose image load failed:', err);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const onLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        // store imageSize convention is [H, W].
        setSideImageSize(side, [img.naturalHeight, img.naturalWidth]);
      }
    },
    [side, setSideImageSize]
  );

  if (!url) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
        no image for frame {currentFrame}
      </div>
    );
  }
  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-red-500">
        image failed to load
      </div>
    );
  }
  if (!src) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
        loading…
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={`${side} frame ${currentFrame}`}
      onLoad={onLoad}
      draggable={false}
      className="block h-full w-full select-none object-contain"
    />
  );
}
